import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Snackbar,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  SaveAlt as SaveAltIcon,
  Mic as MicIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner } from '../../components/common';
import { PDFOverlayForm } from '../../components/documents';
import {
  useTemplateViewModel,
  useDocumentViewModel,
  useConsultationDetailViewModel,
  useConsultationCreateViewModel,
  useSummaryViewModel,
  useTemplatePdfViewModel,
  RecordingWithSTT,
} from '../../viewmodels';
import { FieldMappingItem, FormFieldValue, ExtendedFormData } from '../../models';
import { recordingService, consultationService } from '../../services/consultationService';
import { promptService } from '../../services/promptService';
import { useAuthStore } from '../../hooks/useAuth';
import { Prompt } from '../../models';
import { FormControl, InputLabel, Select, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';

// 폼 데이터를 확장된 형식으로 빌드하는 함수
const buildExtendedFormData = (
  values: Record<string, string>,
  fields: FieldMappingItem[],
  templateId: number | null
): ExtendedFormData => {
  const fieldValues: FormFieldValue[] = fields.map((field) => ({
    field_id: field.field_id,
    hwpx_field: field.hwpx_field || `<<__${field.field_id}__>>`,
    label: field.label,
    type: field.type,
    value: values[field.field_id] || '',
  }));

  return {
    values,
    fields: fieldValues,
    template_id: templateId || undefined,
    updated_at: new Date().toISOString(),
  };
};

// LLM 결과를 템플릿 field_mapping 기반으로 폼 데이터에 매핑하는 함수
const mapLlmResultToFormData = (
  llmResult: Record<string, unknown>,
  fields: FieldMappingItem[],
  currentFormData: Record<string, string>,
  overwriteExisting: boolean = false
): Record<string, string> => {
  const newFormData = { ...currentFormData };

  // 다양한 키 형식을 field_id로 매핑하는 맵 생성
  const keyToFieldIdMap: Record<string, string> = {};
  const fieldIdSet = new Set<string>();
  // 필드 유형 맵: field_id → type (textarea, text, select 등)
  const fieldTypeMap: Record<string, string> = {};

  fields.forEach((field) => {
    fieldIdSet.add(field.field_id);
    fieldTypeMap[field.field_id] = field.type || 'text';

    // 1. label → field_id (한글 라벨)
    if (field.label) {
      keyToFieldIdMap[field.label] = field.field_id;
      // 공백 제거한 버전도 추가
      keyToFieldIdMap[field.label.replace(/\s+/g, '')] = field.field_id;
    }

    // 2. hwpx_field → field_id (HWPX 플레이스홀더 형식)
    if (field.hwpx_field) {
      keyToFieldIdMap[field.hwpx_field] = field.field_id;
      // <<__field_id__>> 형식에서 field_id 추출하여 매핑
      const hwpxMatch = field.hwpx_field.match(/<<__(.+?)__>>/);
      if (hwpxMatch) {
        keyToFieldIdMap[hwpxMatch[1]] = field.field_id;
      }
    }
  });

  // 배열을 문자열로 변환하는 헬퍼 함수
  const arrayToString = (arr: unknown[], fieldType: string): string => {
    const items = arr.filter((item) => typeof item === 'string' || typeof item === 'number');
    // textarea 필드는 줄바꿈으로 연결, 그 외는 쉼표로 연결
    const separator = fieldType === 'textarea' ? '\n' : ', ';
    return items.join(separator);
  };

  // LLM 결과의 각 키-값을 템플릿 필드에 매핑
  Object.entries(llmResult).forEach(([llmKey, llmValue]) => {
    // 먼저 field_id를 찾아서 필드 유형 확인
    let fieldId = keyToFieldIdMap[llmKey] || keyToFieldIdMap[llmKey.replace(/\s+/g, '')];
    if (!fieldId && fieldIdSet.has(llmKey)) {
      fieldId = llmKey;
    }
    const fieldType = fieldId ? fieldTypeMap[fieldId] || 'text' : 'text';

    // 값을 문자열로 변환
    let stringValue: string;
    if (typeof llmValue === 'string') {
      stringValue = llmValue;
    } else if (typeof llmValue === 'number') {
      stringValue = String(llmValue);
    } else if (Array.isArray(llmValue)) {
      // 배열인 경우 필드 유형에 따라 구분자 결정
      stringValue = arrayToString(llmValue, fieldType);
    } else if (llmValue === null || llmValue === undefined) {
      return; // null/undefined는 건너뜀
    } else {
      // 객체 등은 건너뜀
      return;
    }

    if (!stringValue) return;

    if (fieldId) {
      // 필드 ID가 매핑되면 해당 필드에 값 설정
      if (overwriteExisting || !newFormData[fieldId]) {
        newFormData[fieldId] = stringValue;
      }
    } else {
      // 특수 케이스: 상담일자 → year, month, day 분리
      if (llmKey === '상담일자' && stringValue.includes('-')) {
        const parts = stringValue.split('-');
        if (parts.length >= 3) {
          if (overwriteExisting || !newFormData.year) newFormData.year = parts[0];
          if (overwriteExisting || !newFormData.month) newFormData.month = parts[1];
          if (overwriteExisting || !newFormData.day) newFormData.day = parts[2];
        }
      }
    }
  });

  return newFormData;
};


const ConsultationDocumentPage: React.FC = () => {
  const { consultationId } = useParams<{ consultationId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // URL 파라미터
  const templateIdFromUrl = searchParams.get('templateId');
  const recordingIdFromUrl = searchParams.get('recordingId');
  const initialTemplateId = templateIdFromUrl ? parseInt(templateIdFromUrl, 10) : null;
  const initialRecordingId = recordingIdFromUrl ? parseInt(recordingIdFromUrl, 10) : null;
  const isNewConsultation = !consultationId || consultationId === 'new';
  const parsedConsultationId = isNewConsultation ? 0 : parseInt(consultationId!, 10);

  // 상담 기록 ID 상태 (새로 생성 시 업데이트)
  const [currentConsultationId, setCurrentConsultationId] = useState<number>(parsedConsultationId);

  // 템플릿 및 PDF 상태
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(initialTemplateId);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [fields, setFields] = useState<FieldMappingItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // AI 자동채우기 상태
  const [isAutoFilling] = useState(false);

  // STT 텍스트 보기/편집 다이얼로그 상태
  const [sttDialogOpen, setSttDialogOpen] = useState(false);
  const [sttEditMode, setSttEditMode] = useState(false);
  const [sttEditText, setSttEditText] = useState('');
  const [selectedSttRecording, setSelectedSttRecording] = useState<RecordingWithSTT | null>(null);
  const [isSavingStt, setIsSavingStt] = useState(false);

  // WeDMS LLM 자동채우기 상태
  const [llmDialogOpen, setLlmDialogOpen] = useState(false);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<number | ''>('');
  const [llmRole, setLlmRole] = useState('요양보호 상담 전문가');
  const [isLlmProcessing, setIsLlmProcessing] = useState(false);
  const [llmError, setLlmError] = useState<string | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ViewModels
  const { user } = useAuthStore();
  const { templates, isLoading: isLoadingTemplates } = useTemplateViewModel();
  const { consultation, isLoading: isLoadingConsultation, updateFormData, isUpdatingFormData, refetch: refetchConsultation } =
    useConsultationDetailViewModel(currentConsultationId);
  const { createConsultation, isCreating } = useConsultationCreateViewModel();
  useSummaryViewModel(currentConsultationId);
  const { generateDocument, isGenerating: isGeneratingDoc, downloadDocument } = useDocumentViewModel(currentConsultationId);

  // 선택된 템플릿 찾기
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) || null;

  // 템플릿 PDF 로딩 (공통 훅 사용)
  const { pdfUrl, isLoading: isLoadingPdf, error: pdfError, hasPdf } = useTemplatePdfViewModel(selectedTemplate);

  // 프롬프트 목록 로드
  useEffect(() => {
    const loadPrompts = async () => {
      try {
        const promptList = await promptService.getList();
        setPrompts(promptList);
        const defaultPrompt = promptList.find(p => p.is_default);
        if (defaultPrompt) {
          setSelectedPromptId(defaultPrompt.id);
        }
      } catch (err) {
        console.error('Failed to load prompts:', err);
      }
    };
    loadPrompts();
  }, []);

  // 상담 정보와 사용자 프로필로 폼 데이터 초기화
  useEffect(() => {
    if (consultation) {
      const date = new Date(consultation.consultation_date);
      const now = new Date();
      const rawFormData = consultation.form_data || {};

      // 확장된 form_data 구조 지원: values가 있으면 사용, 없으면 레거시 형태로 처리
      const savedFormData = (rawFormData as { values?: Record<string, string> }).values || rawFormData;

      setFormData((prev) => ({
        ...savedFormData,
        year: savedFormData.year || date.getFullYear().toString(),
        month: savedFormData.month || String(date.getMonth() + 1).padStart(2, '0'),
        day: savedFormData.day || String(date.getDate()).padStart(2, '0'),
        hour: savedFormData.hour || String(now.getHours()).padStart(2, '0'),
        consulting_minute: savedFormData.consulting_minute || String(now.getMinutes()).padStart(2, '0'),
        consultant: savedFormData.consultant || user?.name || user?.username || '',
        beneficiary: savedFormData.beneficiary || consultation.client_name || '',
        recipient: savedFormData.recipient || consultation.client_name || '',
        counseling_details: savedFormData.counseling_details || consultation.content || '',
      }));

      // 저장된 템플릿 ID가 있으면 자동 선택 (URL 파라미터가 없는 경우에만)
      if (!initialTemplateId && consultation.template_id) {
        setSelectedTemplateId(consultation.template_id);
      }
    } else if (isNewConsultation) {
      // 새 상담 생성 시 기본값
      const now = new Date();
      setFormData({
        year: now.getFullYear().toString(),
        month: String(now.getMonth() + 1).padStart(2, '0'),
        day: String(now.getDate()).padStart(2, '0'),
        hour: String(now.getHours()).padStart(2, '0'),
        consulting_minute: String(now.getMinutes()).padStart(2, '0'),
        consultant: user?.name || user?.username || '',
      });
    }
  }, [consultation, user, isNewConsultation, initialTemplateId]);

  // 상담 기록의 LLM 결과 자동 로드
  useEffect(() => {
    const loadLlmResult = async () => {
      if (!currentConsultationId || isNewConsultation) return;
      // 템플릿 필드가 로드되지 않았으면 대기
      if (fields.length === 0) return;

      try {
        const llmStatus = await consultationService.getAiAutofillStatus(currentConsultationId);
        if (llmStatus.llm_status === 'completed' && llmStatus.llm_parsed_result) {
          const parsed = llmStatus.llm_parsed_result;
          // 템플릿 field_mapping 기반으로 동적 매핑 (빈 값만 채움)
          setFormData((prev) => mapLlmResultToFormData(parsed, fields, prev, false));
          setSnackbar({ open: true, message: 'AI 분석 결과가 자동으로 적용되었습니다.', severity: 'success' });
        }
      } catch (err) {
        // LLM 상태 조회 실패는 무시
      }
    };
    loadLlmResult();
  }, [currentConsultationId, isNewConsultation, fields]);

  // 템플릿 선택 시 필드 매핑 로드
  useEffect(() => {
    if (selectedTemplate?.field_mapping?.fields?.length) {
      // 템플릿에 저장된 필드 매핑 사용
      setFields(selectedTemplate.field_mapping.fields);
    } else {
      // 필드 매핑이 없는 경우 빈 배열 (경고 메시지 표시됨)
      setFields([]);
    }
  }, [selectedTemplate]);

  // 템플릿에 필드 매핑이 없는지 확인
  const hasNoFieldMappings = selectedTemplate && (!selectedTemplate.field_mapping?.fields?.length);

  const handleFieldChange = useCallback((fieldId: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  // 상담 기록 생성 또는 업데이트
  const handleSaveConsultation = useCallback(async () => {
    setIsSaving(true);
    try {
      // 확장된 폼 데이터 구조 생성 (HWPX 생성에 필요한 메타데이터 포함)
      const extendedFormData = buildExtendedFormData(formData, fields, selectedTemplateId);

      if (isNewConsultation || currentConsultationId === 0) {
        // 새 상담 생성 (template_id, source_recording_id 포함)
        const newConsultation = await createConsultation({
          consultation_date: `${formData.year}-${formData.month}-${formData.day}`,
          title: formData.beneficiary ? `상담 - ${formData.beneficiary}` : '새 상담',
          client_name: formData.beneficiary || '',
          content: formData.counseling_details || '',
          template_id: selectedTemplateId || undefined,
          source_recording_id: initialRecordingId || undefined,
        });

        setCurrentConsultationId(newConsultation.id);

        // 확장된 폼 데이터 및 템플릿 ID 저장
        await updateFormData({ formData: extendedFormData, templateId: selectedTemplateId });

        navigate(`/consultations/${newConsultation.id}/document${selectedTemplateId ? `?templateId=${selectedTemplateId}` : ''}`, { replace: true });

        setSnackbar({ open: true, message: '상담 기록이 생성되었습니다.', severity: 'success' });
      } else {
        // 기존 상담 업데이트 - 확장된 폼 데이터 및 템플릿 ID 저장
        await updateFormData({ formData: extendedFormData, templateId: selectedTemplateId });
        setSnackbar({ open: true, message: '저장되었습니다.', severity: 'success' });
      }
    } catch (err) {
      console.error('Failed to save:', err);
      setSnackbar({ open: true, message: '저장에 실패했습니다.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  }, [isNewConsultation, currentConsultationId, formData, fields, selectedTemplateId, createConsultation, updateFormData, navigate]);

  // WeDMS LLM 기반 자동채우기 (다이얼로그 열기)
  const handleOpenLlmDialog = useCallback(() => {
    if (!currentConsultationId) {
      setError('먼저 상담 기록을 저장해주세요.');
      return;
    }
    setLlmError(null);
    setLlmDialogOpen(true);
  }, [currentConsultationId]);

  // WeDMS LLM 자동채우기 실행 (상담 기록 기준)
  const handleLlmAutoFill = useCallback(async () => {
    if (!currentConsultationId || !selectedPromptId) {
      setLlmError('상담 기록과 프롬프트를 선택해주세요.');
      return;
    }

    setIsLlmProcessing(true);
    setLlmError(null);

    try {
      // 상담 기록에 대해 apply-prompt API 호출
      await consultationService.applyPrompt(currentConsultationId, {
        prompt_id: selectedPromptId as number,
        role: llmRole,
      });

      // 폴링으로 결과 확인
      const pollInterval = setInterval(async () => {
        try {
          const statusResult = await consultationService.getAiAutofillStatus(currentConsultationId);

          if (statusResult.llm_status === 'completed') {
            clearInterval(pollInterval);
            setIsLlmProcessing(false);

            if (statusResult.llm_parsed_result) {
              const parsed = statusResult.llm_parsed_result;
              // 템플릿 field_mapping 기반으로 동적 매핑 (기존 값 덮어쓰기)
              setFormData((prev) => mapLlmResultToFormData(parsed, fields, prev, true));
              setSnackbar({ open: true, message: 'WeDMS AI 자동채우기가 완료되었습니다.', severity: 'success' });
            }
            setLlmDialogOpen(false);
          } else if (statusResult.llm_status === 'failed') {
            clearInterval(pollInterval);
            setIsLlmProcessing(false);
            setLlmError(statusResult.llm_error || 'LLM 처리에 실패했습니다.');
          }
        } catch (pollErr) {
          console.error('LLM status polling failed:', pollErr);
        }
      }, 3000);

      // 5분 타임아웃
      setTimeout(() => {
        clearInterval(pollInterval);
        if (isLlmProcessing) {
          setIsLlmProcessing(false);
          setLlmError('LLM 처리 시간이 초과되었습니다.');
        }
      }, 5 * 60 * 1000);

    } catch (err) {
      setIsLlmProcessing(false);
      const errorMessage = err instanceof Error ? err.message : 'AI 자동채우기 요청에 실패했습니다.';
      setLlmError(errorMessage);
      console.error('LLM auto-fill failed:', err);
    }
  }, [currentConsultationId, selectedPromptId, llmRole, isLlmProcessing, fields]);

  // HWPX 문서 생성
  const handleGenerateDocument = useCallback(async () => {
    if (!currentConsultationId || !selectedTemplateId) {
      setError('상담 및 템플릿을 선택해주세요.');
      return;
    }

    await handleSaveConsultation();

    try {
      const doc = await generateDocument({
        consultation_id: currentConsultationId,
        template_id: selectedTemplateId,
      });

      // 문서 생성 성공 시 자동 다운로드
      if (doc && doc.id) {
        const filename = doc.filename || `document_${doc.id}.hwpx`;
        await downloadDocument(doc.id, filename);
        setSnackbar({
          open: true,
          message: `HWPX 문서가 생성되어 다운로드되었습니다: ${filename}`,
          severity: 'success',
        });
      } else {
        setSnackbar({ open: true, message: 'HWPX 문서가 생성되었습니다.', severity: 'success' });
      }
    } catch (err) {
      console.error('Failed to generate document:', err);
      setError('문서 생성에 실패했습니다.');
    }
  }, [currentConsultationId, selectedTemplateId, generateDocument, downloadDocument, handleSaveConsultation]);

  const handleCloseSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  if (!isNewConsultation && isLoadingConsultation) {
    return <LoadingSpinner />;
  }

  const hasSourceRecording = !!consultation?.source_recording;

  return (
    <Box>
      <PageHeader
        title={isNewConsultation ? '새 상담일지 작성' : '상담일지 문서 작성'}
        breadcrumbs={[
          { label: '상담 기록', path: '/consultations' },
          ...(consultation ? [{ label: consultation.title || '상담', path: `/consultations/${currentConsultationId}` }] : []),
          { label: isNewConsultation ? '새 상담일지' : '문서 작성' },
        ]}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 1. 템플릿 선택 / 녹음 파일 연결 (상단 영역) */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="flex-start">
            {/* 템플릿 선택 */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon fontSize="small" />
                템플릿 선택
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                label="문서 템플릿"
                value={selectedTemplateId || ''}
                onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
                disabled={isLoadingTemplates}
              >
                <MenuItem value="">
                  <em>템플릿을 선택하세요</em>
                </MenuItem>
                {templates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </TextField>
              {selectedTemplate && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {selectedTemplate.description}
                </Typography>
              )}
            </Grid>

            {/* 녹음 파일 STT 보기 */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <MicIcon fontSize="small" />
                녹음 파일
              </Typography>
              {consultation?.source_recording ? (
                <Chip
                  label={
                    consultation.source_recording.stt_status === 'completed'
                      ? '음성 녹음 텍스트 보기'
                      : consultation.source_recording.stt_status === 'processing'
                      ? 'STT 처리 중...'
                      : 'STT 대기'
                  }
                  size="small"
                  color={
                    consultation.source_recording.stt_status === 'completed'
                      ? 'success'
                      : consultation.source_recording.stt_status === 'processing'
                      ? 'warning'
                      : 'default'
                  }
                  icon={<VisibilityIcon fontSize="small" />}
                  onClick={
                    consultation.source_recording.stt_status === 'completed'
                      ? () => {
                          const rec = consultation.source_recording!;
                          setSelectedSttRecording({
                            ...rec,
                            sttText: rec.stt_text || '',
                          });
                          setSttEditText(rec.stt_text || '');
                          setSttEditMode(false);
                          setSttDialogOpen(true);
                        }
                      : undefined
                  }
                  disabled={consultation.source_recording.stt_status !== 'completed'}
                  sx={consultation.source_recording.stt_status === 'completed' ? { cursor: 'pointer' } : undefined}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  연결된 녹음 파일이 없습니다
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 2. PDF 오버레이 폼 (중앙 영역 - 상담 내용 포함, AI 요약 버튼 포함) */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 0 }}>
          {/* AI 버튼 툴바 */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              상담일지 작성
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                color="primary"
                startIcon={isAutoFilling || isLlmProcessing ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={handleOpenLlmDialog}
                disabled={isAutoFilling || isLlmProcessing || !selectedTemplateId || !hasSourceRecording}
              >
                AI 자동채우기
              </Button>
            </Box>
          </Box>

          {/* PDF 오버레이 또는 빈 상태 */}
          <Box sx={{ p: 2 }}>
            {isLoadingPdf ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, gap: 2 }}>
                <CircularProgress />
                <Typography>PDF 로딩 중...</Typography>
              </Box>
            ) : pdfUrl && hasPdf ? (
              <Box>
                {/* 필드 매핑이 없는 경우 경고 메시지 */}
                {hasNoFieldMappings && (
                  <Alert severity="info" sx={{ mb: 2, mx: 'auto', maxWidth: 850 }}>
                    <Typography variant="body2">
                      이 템플릿에는 입력 필드가 정의되어 있지 않습니다.
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      템플릿 관리 &gt; 필드 편집에서 입력 필드 위치를 지정하면 PDF 위에 입력 폼이 표시됩니다.
                    </Typography>
                  </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <PDFOverlayForm
                    pdfUrl={pdfUrl}
                    fields={fields}
                    formData={formData}
                    onChange={handleFieldChange}
                    width={850}
                  />
                </Box>
              </Box>
            ) : selectedTemplate && pdfError ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  color: 'text.secondary',
                }}
              >
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {pdfError}
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  템플릿 관리에서 HWPX 파일을 다시 업로드하면 PDF 미리보기가 가능합니다.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 8,
                  color: 'text.secondary',
                }}
              >
                <DescriptionIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                <Typography variant="h6" gutterBottom>
                  템플릿을 선택하면 PDF 미리보기가 표시됩니다
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  상단에서 문서 템플릿을 선택하고, 녹음 파일을 연결하여 상담일지를 작성하세요.
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* 3. 저장 / HWPX 생성 버튼 (하단 영역) */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={isSaving || isUpdatingFormData || isCreating ? <CircularProgress size={20} /> : <SaveAltIcon />}
              onClick={handleSaveConsultation}
              disabled={isSaving || isUpdatingFormData || isCreating}
              sx={{ minWidth: 160 }}
            >
              {isSaving || isUpdatingFormData || isCreating ? '저장 중...' : '저장'}
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={isGeneratingDoc ? <CircularProgress size={20} /> : <DescriptionIcon />}
              onClick={handleGenerateDocument}
              disabled={!selectedTemplateId || isGeneratingDoc || (isNewConsultation && currentConsultationId === 0)}
              sx={{ minWidth: 200 }}
            >
              {isGeneratingDoc ? '생성 중...' : 'HWPX 문서 생성'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* AI 자동채우기 다이얼로그 */}
      <Dialog open={llmDialogOpen} onClose={() => !isLlmProcessing && !isAutoFilling && setLlmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          AI 자동채우기
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            녹음 파일의 STT 텍스트를 AI가 분석하여 상담일지 필드를 자동으로 채웁니다.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>프롬프트 선택</InputLabel>
              <Select
                value={selectedPromptId}
                label="프롬프트 선택"
                onChange={(e) => setSelectedPromptId(e.target.value as number)}
                disabled={isLlmProcessing || isAutoFilling}
              >
                {prompts.map((prompt) => (
                  <MenuItem key={prompt.id} value={prompt.id}>
                    {prompt.name} {prompt.is_default && '(기본)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="시스템 역할"
              value={llmRole}
              onChange={(e) => setLlmRole(e.target.value)}
              fullWidth
              size="small"
              disabled={isLlmProcessing || isAutoFilling}
              helperText="AI의 역할을 정의합니다"
            />
            {llmError && (
              <Alert severity="error">
                {llmError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLlmDialogOpen(false)} disabled={isLlmProcessing || isAutoFilling}>
            취소
          </Button>
          <Button
            onClick={handleLlmAutoFill}
            variant="contained"
            color="primary"
            disabled={isLlmProcessing || isAutoFilling || !selectedPromptId}
            startIcon={isLlmProcessing || isAutoFilling ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
          >
            {isLlmProcessing || isAutoFilling ? 'AI 분석 중...' : '자동채우기'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* STT 텍스트 보기/편집 다이얼로그 */}
      <Dialog
        open={sttDialogOpen}
        onClose={() => !isSavingStt && setSttDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MicIcon color="primary" />
            <Typography variant="h6">
              STT 텍스트 {sttEditMode ? '편집' : '보기'}
            </Typography>
            {selectedSttRecording && (
              <Chip label={selectedSttRecording.filename} size="small" color="primary" variant="outlined" />
            )}
          </Box>
          <IconButton
            onClick={() => setSttDialogOpen(false)}
            disabled={isSavingStt}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {sttEditMode ? (
            <TextField
              multiline
              fullWidth
              minRows={15}
              maxRows={25}
              value={sttEditText}
              onChange={(e) => setSttEditText(e.target.value)}
              disabled={isSavingStt}
              placeholder="STT 텍스트를 편집하세요..."
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }
              }}
            />
          ) : (
            <Box
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '0.9rem',
                lineHeight: 1.6,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                minHeight: 300,
                maxHeight: 500,
                overflow: 'auto',
              }}
            >
              {selectedSttRecording?.stt_text || '(STT 텍스트가 없습니다)'}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {sttEditMode ? (
            <>
              <Button
                onClick={() => {
                  setSttEditMode(false);
                  setSttEditText(selectedSttRecording?.stt_text || '');
                }}
                disabled={isSavingStt}
              >
                취소
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  if (!selectedSttRecording) return;
                  setIsSavingStt(true);
                  try {
                    await recordingService.updateSttText(selectedSttRecording.id, sttEditText);
                    // 로컬 상태 업데이트
                    setSelectedSttRecording({ ...selectedSttRecording, stt_text: sttEditText });
                    refetchConsultation();
                    setSttEditMode(false);
                    setSnackbar({ open: true, message: 'STT 텍스트가 저장되었습니다.', severity: 'success' });
                  } catch (err) {
                    setSnackbar({ open: true, message: 'STT 텍스트 저장에 실패했습니다.', severity: 'error' });
                  } finally {
                    setIsSavingStt(false);
                  }
                }}
                disabled={isSavingStt}
                startIcon={isSavingStt ? <CircularProgress size={16} /> : <SaveAltIcon />}
              >
                {isSavingStt ? '저장 중...' : '저장'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setSttDialogOpen(false)}>
                닫기
              </Button>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  setSttEditText(selectedSttRecording?.stt_text || '');
                  setSttEditMode(true);
                }}
              >
                편집
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConsultationDocumentPage;
